import { useState, useEffect } from 'react';
import { getSessionStatus, ApiError } from '../lib/api';
import { safeLocalStorage, safeSessionStorage } from '../lib/safeStorage';

export interface ExistingSessionInfo {
  sessionId: string;
  partner: 'A' | 'B';
  partnerAName: string;
  partnerBName: string;
  state: 'quiz' | 'waiting' | 'results';
  resumeUrl: string;
}

interface UseExistingSessionReturn {
  session: ExistingSessionInfo | null;
  isChecking: boolean;
  dismiss: () => void;
}

function findSessionIds(): string[] {
  const ids: string[] = [];
  for (const key of safeLocalStorage.keys()) {
    // Match quiz_token_<uuid> but NOT quiz_token_b_<uuid>
    const match = key.match(/^quiz_token_(?!b_)(.+)$/);
    if (match && match[1]) {
      ids.push(match[1]);
    }
  }
  return ids;
}

function detectPartnerSlot(sessionId: string): 'A' | 'B' {
  // Partner A is identified by having stored the Partner B token for sharing
  if (safeLocalStorage.getItem(`quiz_token_b_${sessionId}`)) {
    return 'A';
  }
  if (safeLocalStorage.getItem(`quiz_progress_${sessionId}_A`)) return 'A';
  if (safeLocalStorage.getItem(`quiz_progress_${sessionId}_B`)) return 'B';
  // Default to B (Partner B arrives via share link, doesn't store quiz_token_b_)
  return 'B';
}

function cleanupSession(sessionId: string) {
  safeLocalStorage.removeItem(`quiz_token_${sessionId}`);
  safeLocalStorage.removeItem(`quiz_token_b_${sessionId}`);
  safeLocalStorage.removeItem(`quiz_progress_${sessionId}_A`);
  safeLocalStorage.removeItem(`quiz_progress_${sessionId}_B`);
}

export default function useExistingSession(): UseExistingSessionReturn {
  const [session, setSession] = useState<ExistingSessionInfo | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      const sessionIds = findSessionIds();

      for (const sessionId of sessionIds) {
        // Skip if dismissed this browser session
        if (safeSessionStorage.getItem(`quiz_banner_dismissed_${sessionId}`)) {
          continue;
        }

        try {
          const data = await getSessionStatus(sessionId);
          if (cancelled) return;

          const partner = detectPartnerSlot(sessionId);
          const myCompleted = partner === 'A' ? data.partnerACompleted : data.partnerBCompleted;
          const token = safeLocalStorage.getItem(`quiz_token_${sessionId}`);
          const tokenParam = token ? `?t=${token}` : '';

          let state: ExistingSessionInfo['state'];
          let resumeUrl: string;

          if (data.bothCompleted) {
            state = 'results';
            resumeUrl = `/results/${sessionId}${tokenParam}`;
          } else if (myCompleted) {
            state = 'waiting';
            resumeUrl = `/waiting/${sessionId}/${partner}${tokenParam}`;
          } else {
            state = 'quiz';
            resumeUrl = `/quiz/${sessionId}/${partner}${tokenParam}`;
          }

          setSession({
            sessionId,
            partner,
            partnerAName: data.partnerAName,
            partnerBName: data.partnerBName,
            state,
            resumeUrl,
          });
          setIsChecking(false);
          return;
        } catch (err) {
          if (err instanceof ApiError && err.status === 404) {
            cleanupSession(sessionId);
            continue;
          }
          // Network error — skip silently
          continue;
        }
      }

      if (!cancelled) {
        setIsChecking(false);
      }
    }

    check();
    return () => { cancelled = true; };
  }, []);

  function dismiss() {
    if (session) {
      safeSessionStorage.setItem(`quiz_banner_dismissed_${session.sessionId}`, '1');
    }
    setSession(null);
  }

  return { session, isChecking, dismiss };
}
